<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useCreateSite } from "@/api/site-query";
import { useSnackbarStore } from "@/stores";
import { useScopeStore } from "@/stores/scope";
import { useForm } from "@soft-db/form";
import { CreateSite, Site } from "@soft-db/monitoring-api-client";
import { FormField, Icon, InputGroup, TextInput } from "@soft-db/ui";
import { useI18n } from "vue-i18n";
import { z } from "zod";
import AppDialog from "../common/AppDialog.vue";
import { icPlaceOutline } from "../icons";
import DistScope from "./components/DistScope.vue";
import SpaceScope from "./components/SpaceScope.vue";

const dialog = defineModel<boolean>("dialog");

const emit = defineEmits<{
	created: [id: string];
}>();

const { t } = useI18n();

const { currentUser } = useCurrentUser();
const siteCreation = useCreateSite();
const snackbarStore = useSnackbarStore();

const { useField, handleSubmit, clear } = useForm<CreateSite>();
const name = useField("name", { schema: z.string().trim().min(1) });
const description = useField("description", { isRequired: false, initialValue: "" });

const scopeStore = useScopeStore();
const scope = toRef(scopeStore, "scope");

const createSite = handleSubmit(async (form) => {
	if (!currentUser.value) return;

	await siteCreation.mutateAsync({ scope: scope.value, data: form }).then((site: Site) => {
		snackbarStore.openOnMessage(t("snackbar.siteCreated"));
		emit("created", site.id);
		close();
	});
});

function close() {
	dialog.value = false;
	clear();
}
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[500px]"
		:actionText="t('common.create')"
		@click:action="createSite"
		@close="close"
	>
		<template #title>
			<Icon :d="icPlaceOutline" />
			<span>{{ t("actions.createSite") }} </span>
		</template>
		<template #content>
			<SpaceScope v-if="scope.type === 'space'" :id="scope.id" />
			<DistScope v-if="scope.type === 'distributor'" :id="scope.id" />

			<FormField :label="t('common.name')" :errors="name.errors.value">
				<InputGroup>
					<TextInput v-model="name.data.value" />
				</InputGroup>
			</FormField>
			<FormField :label="t('common.description')" :errors="description.errors.value">
				<InputGroup>
					<TextInput v-model="description.data.value" />
				</InputGroup>
			</FormField>
		</template>
	</AppDialog>
</template>
